import {
  CheckoutButtonSize,
  CheckoutPaymentOptionsOtherThanPayPal,
  CheckoutSingleButtonColorType
} from '@components/buttons/types';
import { OptionalPaymentMethods } from '@components/Settings/types';

export enum OrderStatus {
  CREATED = 'CREATED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PAYER_ACTION_REQUIRED = 'PAYER_ACTION_REQUIRED'
}

export enum CaptureStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  DECLINED = 'DECLINED',
  AUTH_FAILED = 'AUTH_FAILED',
  CART_EMPTY = 'CART_EMPTY',
  CART_UPDATED = 'CART_UPDATED'
}

export enum ButtonType {
  FIXED_PRICE = 'FIXED_PRICE',
  VARIABLE_PRICE = 'VARIABLE_PRICE',
  SINGLE_SELECT_LIST = 'SINGLE_SELECT_LIST'
}

export enum IntegrationOptions {
  ADD_TO_CART = 'addToCart',
  PAYMENT_LINK = 'paymentLink',
  QR_CODE = 'qrCode',
  STACKED_BUTTONS = 'stackedButtons',
  SINGLE_BUTTON = 'singleButton'
}

const cardImg = 'https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/cards.svg';

export const INTEGRATION_TYPE_TO_IMAGE = {
  [IntegrationOptions.PAYMENT_LINK]:
    'https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/fun.svg',
  [IntegrationOptions.QR_CODE]:
    'https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/qrc-pay.svg',
  [IntegrationOptions.STACKED_BUTTONS]: cardImg,
  [IntegrationOptions.SINGLE_BUTTON]: cardImg,
  [IntegrationOptions.ADD_TO_CART]:
    'https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/cart.svg'
};

export const PAYMENT_IMAGE_SOURCE = {
  [OptionalPaymentMethods.PAYLATER]:
    'https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg',
  [OptionalPaymentMethods.VENMO]:
    'https://www.paypalobjects.com/paypal-ui/logos/svg/venmo-mark-color.svg',
  [OptionalPaymentMethods.APPLEPAY]:
    'https://www.paypalobjects.com/paypal-ui/logos/svg/apple-pay-color.svg',
  [OptionalPaymentMethods.GOOGLEPAY]:
    'https://www.paypalobjects.com/paypal-ui/logos/svg/google-pay-color.svg'
};

export const PaymentMethodLogoUrlMapper = {
  [OptionalPaymentMethods.PAYLATER]: PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.PAYLATER],
  [OptionalPaymentMethods.VENMO]: PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.VENMO],
  [OptionalPaymentMethods.APPLEPAY]: PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.APPLEPAY],
  [OptionalPaymentMethods.GOOGLEPAY]: PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.GOOGLEPAY]
};

export const PaymentMethodLogoSVGUrlMapper = {
  [CheckoutPaymentOptionsOtherThanPayPal.Venmo]: PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.VENMO],
  [CheckoutPaymentOptionsOtherThanPayPal.PayLater]:
    PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.PAYLATER],
  [CheckoutPaymentOptionsOtherThanPayPal.ApplePay]:
    PAYMENT_IMAGE_SOURCE[OptionalPaymentMethods.APPLEPAY]
};

export const Color = {
  [CheckoutSingleButtonColorType.Recommended]: '#FFD140',
  [CheckoutSingleButtonColorType.Black]: '#000000',
  [CheckoutSingleButtonColorType.White]: '#ffffff'
};

export enum PreviewTabName {
  PRODUCT = 'product',
  CHECK_OUT = 'checkout',
  THANK_YOU = 'thankyou',
  STACKED_BUTTONS = 'stackedButtons',
  SINGLE_BUTTON = 'singleButton',
  VIEW_CART = 'viewCart',
  SUBSCRIPTIONS = 'subscriptions'
}

export const INVALID_OPTION_CONTENT_KEY = 'error.text.buyerInvalidOption';
export const INVALID_QUANTITY_CONTENT_KEY = 'error.cart.quantityExceeded';

export const CardSVGUrl = {
  cardSVGUrlWithApplePay: 'https://www.paypalobjects.com/images/Debit_Credit_APM.svg',
  cardSVGUrlWithOutApplePay: 'https://www.paypalobjects.com/images/Debit_Credit.svg'
};

interface IButtonProps {
  minWidth: string;
  maxWidth: string;
  horizontalWidth: string;
  lineHeight: string;
  fontWeight: number;
  sdkFontSize: string;
  sdkHeight: string;
  singleButtonFontSize: string;
  singleButtonHeight: string;
}

// reference: https://www.figma.com/design/2R4zzzptJETTxjvplCSTTM/No-Code-Checkout-2.0?node-id=13514-107034&m=dev
// checkout button requires height in px unit to match SDK buttons, which only accept px values
export const ButtonSizes: Record<string, IButtonProps> = {
  [CheckoutButtonSize.Large]: {
    minWidth: '11.625rem',
    maxWidth: '31.1875rem',
    horizontalWidth: '33.75rem',
    lineHeight: '1.5rem',
    fontWeight: 700,
    sdkFontSize: '1.125rem',
    sdkHeight: '50px',
    singleButtonFontSize: '1.125rem',
    singleButtonHeight: '3.125rem'
  },
  [CheckoutButtonSize.Medium]: {
    minWidth: '11.625rem',
    maxWidth: '24.9375rem',
    horizontalWidth: '30.625rem',
    lineHeight: '1.25rem',
    fontWeight: 700,
    sdkFontSize: '1rem',
    sdkHeight: '42px',
    singleButtonFontSize: '1rem',
    singleButtonHeight: '2.625rem'
  },
  [CheckoutButtonSize.Small]: {
    minWidth: '11.625rem',
    // need this to be 316px instead of 299px since minimum JS SDK button width is 150
    // so when 2 horizontal buttons are rendered side by side, it could be up to 150+150+16(gap)
    maxWidth: '19.75rem',
    horizontalWidth: '25rem',
    lineHeight: '1.125rem',
    fontWeight: 700,
    sdkFontSize: '0.75rem',
    sdkHeight: '32px',
    singleButtonFontSize: '0.875rem',
    singleButtonHeight: '2rem'
  }
};

type venmoColors = 'gold' | 'blue' | 'silver' | 'white' | 'black';

export const VenmoColorMapping: Record<string, venmoColors | undefined> = {
  gold: undefined,
  blue: 'silver',
  silver: 'blue',
  black: 'black',
  white: 'white'
};

interface IColorProps {
  backgroundColor: string;
  color: string;
  hoverBrightness: number;
  border?: string;
  focus?: {
    outline: string;
    offset: string;
  };
}

export const checkoutColorMapping: Record<string, IColorProps> = {
  default: {
    backgroundColor: '#001435',
    color: 'white',
    hoverBrightness: 1.5,
    focus: {
      outline: 'solid 0.3rem #009cde',
      offset: '-0.3rem'
    }
  },
  black: {
    color: 'white',
    backgroundColor: '#2c2e2f',
    hoverBrightness: 1.2
  },
  white: {
    color: '#2c2e2f',
    backgroundColor: 'white',
    border: '0.0625rem solid #555',
    hoverBrightness: 0.95
  }
};

export enum CodeLanguages {
  HTML = 'html',
  REACT = 'react'
}

export const PREVIEW_CART_ITEM_ID = 'preview_cart_item_id';

export enum MediaErrorCode {
  HIT_CSAM = 406,
  NOT_FOUND = 404,
  DIMENSION_TOO_LOW = 424,
  DIMENSION_TOO_HIGH = 425
}

export const PRODUCT_DESCRIPTION_MAX_LENGTH = 2048;

export const MaxProductImageNum = 5;
export const MaxImageSize = 10 * 1000 * 1000; // File input uses the SI prefix convention of 1KB = 1000B, similar to macOS.

export const ALLOWED_IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'bmp'];
export const VARIANTS_LIMIT = 3;
export const VARIANTS_OPTIONS_LIMIT = 10;
export const SectionFieldRequiredGenericContentKey = 'editor.error.requiredSectionField';

export const UploadImageErrorDescriptions = {
  invalidFile: 'UPLOAD_FILE_FORMAT_INVALID',
  hitCSAM: 'HIT_CSAM_ACCOUNT_RESTRICTED',
  dimensionLowError: 'DIMENSION_TOO_LOW_ERROR',
  dimensionHighError: 'DIMENSION_TOO_HIGH_ERROR',
  serverError: 'SERVER_ERROR',
  sizeError: 'SIZE_ERROR',
  typeError: 'TYPE_ERROR'
};

export const MAX_CROSS_SELL_AMOUNT = 5;
// SoldOutUrl is no need to be supported in UI at this moment. It can be dummy url by now.
export const DummySoldOutUrl = 'https://www.paypal.com';
export const MAX_CROSS_SELL_CART_ITEMS = 20;
export const VARIANT_DROPDOWN_ID_PREFIX = 'dropdownMenuButton_variant-dropdown';

export const BUTTON_PREVIEW_IFRAME_ID = 'button-preview-iframe';
